import type {
  BusFilterType,
  GroupResponsibleState,
  Participant,
  ParticipantFilterType,
  Room,
  RoomFilterType,
} from '@/scopes/account/group-responsible/domain/types'

interface SortableParticipant {
  assigned: boolean
  name: string
}

function sortParticipants<T extends SortableParticipant>(a: T, b: T): number {
  if (a.assigned !== b.assigned) {
    return a.assigned ? -1 : 1
  }
  return a.name.localeCompare(b.name)
}

function sortRooms(rooms: Room[], participants: Participant[]): Room[] {
  return rooms.sort((a, b) => {
    const aHasAssigned = roomHasAssignedParticipants(a, participants)
    const bHasAssigned = roomHasAssignedParticipants(b, participants)

    if (aHasAssigned !== bHasAssigned) {
      return aHasAssigned ? -1 : 1
    }

    return a.name.localeCompare(b.name)
  })
}

function roomHasAssignedParticipants(room: Room, participants: Participant[]): boolean {
  return room.participantsId.some(participantId =>
    participants.some(participant => Number(participant.id) === participantId && participant.assigned),
  )
}

export function isBookingTabActive(state: GroupResponsibleState) {
  return state.activeTab === 'booking'
}

export function isParticipantsTabActive(state: GroupResponsibleState) {
  return state.activeTab === 'participants'
}

export function isRoomsTabActive(state: GroupResponsibleState) {
  return state.activeTab === 'rooms'
}

export function isBusTabActive(state: GroupResponsibleState) {
  return state.activeTab === 'bus'
}

export function getParticipantsWithBusInfo(state: GroupResponsibleState) {
  const { participants, buses } = state
  if (!participants)
    return []

  return Object.entries(participants).map(([participantId, participant]) => {
    const associatedBuses = Object.values(buses).filter(bus => bus.participantsId.includes(Number(participantId)))

    const outboundBus = associatedBuses.find(bus => bus.direction === 'Outbound')
    const inboundBus = associatedBuses.find(bus => bus.direction === 'Inbound')
    const hasLocation = outboundBus?.departureLocation || inboundBus?.arrivalLocation
    const location = hasLocation
      ? [outboundBus?.departureLocation, inboundBus?.arrivalLocation].filter(Boolean).join(' - ')
      : '-'
    const hasNumber = outboundBus?.busNumber || inboundBus?.busNumber
    const busNumber = hasNumber ? [outboundBus?.busNumber, inboundBus?.busNumber].filter(Boolean).join(' - ') : '-'

    const label = (() => {
      if (associatedBuses.length) {
        return {
          state: 'success',
          text: 'groups_responsible_status_booked',
        }
      }

      if (participant.assigned) {
        return {
          state: 'info',
          text: 'groups_responsible_status_not_applicable',
        }
      }

      return {
        state: 'error',
        text: 'groups_responsible_status_not_booked',
      }
    })()

    const participantData = {
      id: participantId,
      name: participant.name,
      email: participant.email,
      assigned: participant.assigned,
      isBusAssignmentProcessing: participant.isBusAssignmentProcessing,
      busInfo: associatedBuses.length
        ? {
            number: busNumber,
            location,
          }
        : null,
      label,
    }

    return participantData
  })
}

export function getParticipantExtraInfo(state: GroupResponsibleState) {
  const activeParticipant = state.activeExtraInfoId ? state.participants[state.activeExtraInfoId] : null
  let roomData

  if (state.rooms) {
    roomData = Object.values(state.rooms).find((room) => {
      return room.participantsId.includes(Number(activeParticipant?.id))
    })
  }

  return {
    name: activeParticipant?.name || '',
    bookingNumber: activeParticipant?.bookingNumber,
    email: activeParticipant?.email || '-',
    room: activeParticipant?.room || '-',
    mobileNumber: activeParticipant?.mobileNumber,
    telephoneNumber: activeParticipant?.telephoneNumber,
    skiPass: activeParticipant?.skiPass || '-',
    hasSkiMaterial: activeParticipant?.hasSkiMaterial ? 'groups_responsible_yes' : 'groups_responsible_no',
    hasTotallySweater: activeParticipant?.hasTotallySweater ? 'groups_responsible_yes' : 'groups_responsible_no',
    accommodationName: roomData?.accommodationName || '-',
    roomType: roomData?.type || '-',
  }
}

export function getRoomExtraInfo(state: GroupResponsibleState) {
  const activeRoom = state.activeExtraInfoId ? state.rooms[state.activeExtraInfoId] : null

  return {
    name: activeRoom?.name || '',
    participants: activeRoom?.participants || [],
    type: activeRoom?.type || '-',
    accommodationName: activeRoom?.accommodationName || '-',
  }
}

export function getParticipantCounts(state: GroupResponsibleState): {
  all: number
  notPaid: number
  partialPaid: number
  paid: number
} {
  const participants = Object.values(state.participants)

  const all = participants.length
  const notPaid = participants.filter(p => p.status.text === 'groups_responsible_not_paid').length
  const partialPaid = participants.filter(p => p.status.text === 'groups_responsible_partial_paid').length
  const paid = participants.filter(p => p.status.text === 'groups_responsible_paid').length

  return { all, notPaid, partialPaid, paid }
}

export function getParticipantsBusBookedCounts(state: GroupResponsibleState): {
  all: number
  notBooked: number
  booked: number
} {
  const { participants, buses } = state
  if (!participants)
    return { all: 0, notBooked: 0, booked: 0 }

  const all = Object.keys(participants).length
  const notAssigned = Object.values(participants).filter(p => !p.assigned).length

  const booked = Object.keys(participants).filter(participantId =>
    Object.values(buses).some(bus => bus.participantsId.includes(Number(participantId))),
  ).length

  return { all, notBooked: notAssigned, booked }
}

export function getFilteredParticipants(state: GroupResponsibleState, filter: ParticipantFilterType, searchTerm = '') {
  const trimmedSearchTerm = searchTerm.trim().toLowerCase()
  return Object.values(state.participants)
    .filter((participant) => {
      const matchesStatus = filter === 'all' || participant.status.text === `groups_responsible_${filter}`
      const matchesSearch = trimmedSearchTerm === '' || participant.name.toLowerCase().includes(trimmedSearchTerm)
      return matchesStatus && matchesSearch
    })
    .sort(sortParticipants)
}

export function getFilteredParticipantsBusBooked(state: GroupResponsibleState, filter: BusFilterType, searchTerm = '') {
  const participantsWithBusInfo = getParticipantsWithBusInfo(state)
  const trimmedSearchTerm = searchTerm.trim().toLowerCase()

  const filteredParticipants = participantsWithBusInfo.filter((participant) => {
    const matchesFilter = filter === 'all' || participant.label.text === `groups_responsible_status_${filter}`

    const matchesSearch = trimmedSearchTerm === '' || participant.name.toLowerCase().includes(trimmedSearchTerm)

    return matchesFilter && matchesSearch
  })

  return filteredParticipants.sort(sortParticipants)
}

export function getRoomsOccupiedCounts(state: GroupResponsibleState): {
  all: number
  notOccupied: number
  occupied: number
} {
  const { rooms } = state
  if (!rooms)
    return { all: 0, notOccupied: 0, occupied: 0 }

  const all = Object.keys(rooms).length

  const notOccupied = Object.keys(rooms).filter(
    room => rooms[room].occupancy.occupied !== rooms[room].occupancy.max,
  ).length

  const occupied = all - notOccupied

  return { all, notOccupied, occupied }
}

export function getFilteredRoomsOccupied(state: GroupResponsibleState, filter: RoomFilterType, searchTerm = '') {
  const rooms = Object.values(state.rooms)
  const participants = Object.values(state.participants)
  const trimmedSearchTerm = searchTerm.trim().toLowerCase()

  let filteredRooms = rooms

  if (filter !== 'all') {
    filteredRooms = rooms.filter((room) => {
      const { occupied, max } = room.occupancy
      return filter === 'occupied' ? occupied === max : occupied !== max
    })
  }

  if (trimmedSearchTerm) {
    filteredRooms = filteredRooms.filter((room) => {
      const nameMatches = room.name.toLowerCase().includes(trimmedSearchTerm)
      if (nameMatches)
        return true

      const roomParticipants = participants.filter((participant) => {
        return room.participantsId.includes(Number(participant.id))
      })

      return roomParticipants.some(participant => participant.name.toLowerCase().includes(trimmedSearchTerm))
    })
  }

  return sortRooms(filteredRooms, participants)
}

export function getBusAssignmentDeadLine(state: GroupResponsibleState, daysBefore: number, locale: string): string {
  if (daysBefore < 0)
    return ''
  const startingDate = state.bookingData?.group?.startingDate
  if (!startingDate)
    return ''

  const date = new Date(startingDate)
  if (Number.isNaN(date.getTime()))
    return ''

  const limitDate = new Date(date)
  limitDate.setDate(date.getDate() - daysBefore)

  try {
    return limitDate.toLocaleDateString(locale, {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }
  catch {
    return limitDate.toLocaleDateString('en-US', {
      day: 'numeric',
      month: 'long',
      year: 'numeric',
    })
  }
}
